/**
 * Quy trình lô thành phẩm (một nguồn cho màn KCS, Đóng gói, Giao hàng):
 *   KCS Đạt / Nhân nhượng (bộ phận QC — chỉ kết luận chất lượng)
 *   → Phiếu đóng gói Chờ đóng gói (bộ phận Sản xuất: SL đạt / lỗi khi đóng gói, người đóng gói, tem thùng, nhập kho)
 *   → Chờ giao hàng / Chờ xe → Lên phiếu giao.
 * Lô đã lên phiếu giao đủ số lượng thì không còn đóng gói / tạo phiếu giao; gom một phiếu giao chỉ khi cùng khách hàng.
 *
 * Hàm nhận được cả dòng phiếu KCS (có item.packingSlip) lẫn dòng phiếu đóng gói (có item.qcFinal).
 */
export type FqcStage = "PENDING_RECEIPT" | "WAITING_DELIVERY" | "READY_FOR_DISPATCH" | "HAS_DELIVERY";

/** Phiếu đóng gói của dòng (dòng KCS → item.packingSlip; dòng phiếu đóng gói → chính nó) */
export const packingSlipOf = (item: any) => (item?.qcFinal ? item : item?.packingSlip) || null;
/** Kết quả KCS của dòng */
const qcStatusOf = (item: any) => (item?.qcFinal ? item.qcFinal.status : item?.status);

export const isFqcAccepted = (item: any) => qcStatusOf(item) === "PASSED" || qcStatusOf(item) === "WARNING";

export function fqcStageOf(item: any): FqcStage | null {
  if (!isFqcAccepted(item)) return null;
  const slip = packingSlipOf(item);
  if (item?.deliveryInfo?.hasDelivery) return "HAS_DELIVERY";
  if (slip?.status !== "PACKED") return "PENDING_RECEIPT";
  if (slip?.readyForDispatchAt || item?.deliveryInfo?.isReadyForDispatch) return "READY_FOR_DISPATCH";
  return "WAITING_DELIVERY";
}

/** Còn đóng gói & nhập kho được: lô Đạt, phiếu đóng gói chưa đóng gói, chưa lên phiếu giao */
export const canPackFqc = (item: any) => fqcStageOf(item) === "PENDING_RECEIPT";

/** Tạo phiếu giao được: đã đóng gói & nhập kho và còn hàng chưa lên phiếu giao */
export const canDeliverFqc = (item: any) => {
  const stage = fqcStageOf(item);
  return stage === "WAITING_DELIVERY" || stage === "READY_FOR_DISPATCH";
};

/** Id lô KCS của dòng (phiếu giao gom theo lô KCS: ?fqcBatchIds=) */
export const fqcLotIdOf = (item: any) => (item?.qcFinal ? item.qcFinalId || item.qcFinal.id : item?.id);

export function fqcCustomerOf(item: any): { id: string | null; name: string } {
  const po = item?.productionOrder;
  // Lô gia công 100% không có lệnh SX: khách của biên bản FQC (phiếu đóng gói → qcFinal.customer)
  const c = po?.customer || item?.customer || item?.qcFinal?.customer;
  return {
    id: po?.customerId || c?.id || item?.customerId || item?.qcFinal?.customerId || null,
    name: c?.shortName || c?.name || item?.customerName || "",
  };
}

/** Gom lô vào một phiếu giao: trả lỗi khi các lô thuộc nhiều khách hàng */
export function fqcDeliveryGroupError(items: any[]): string | null {
  const customers = new Map<string, string>();
  for (const it of items) {
    const c = fqcCustomerOf(it);
    customers.set(c.id || c.name || "?", c.name || "Chưa có khách hàng");
  }
  if (customers.size <= 1) return null;
  return `Chỉ gom phiếu giao khi cùng khách hàng — đang chọn ${customers.size} khách hàng: ${Array.from(customers.values()).join(", ")}`;
}
